import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { useBrand } from '../context/BrandContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const Login = () => {
  const { brandName } = useBrand();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('login'); // login | phone | 2fa
  const [phone, setPhone] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = { email, password };

      if (step === 'phone') {
        // Submitting phone number to save + send code
        payload.phone = phone;
      }

      if (step === '2fa') {
        payload.two_fa_code = twoFACode;
      }

      const response = await axios.post(`${API_BASE}/auth/login`, payload);

      if (response.data.needs_phone) {
        // No phone on file — prompt to add
        setStep('phone');
        setStatusMessage(response.data.message);
        setLoading(false);
        return;
      }

      if (response.data.requires_2fa) {
        // Code sent — enter it
        setStep('2fa');
        setStatusMessage(response.data.message);
        setLoading(false);
        return;
      }

      if (response.data.success) {
        const { user, token } = response.data;
        localStorage.setItem('mcaToken', token);
        localStorage.setItem('mcaUser', JSON.stringify(user));
        sessionStorage.setItem('mcaToken', token);
        sessionStorage.setItem('mcaUser', JSON.stringify(user));
        window.location.replace('/funded-deals');
      } else {
        setError('Login failed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('login');
    setTwoFACode('');
    setPhone('');
    setStatusMessage('');
    setError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-orange-100">
      <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-500 text-white rounded-2xl text-2xl font-bold mb-4">
            {(brandName || 'D').trim().charAt(0).toUpperCase()}
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2" data-testid="login-brand-name">{brandName}</h1>
          <p className="text-gray-600">
            {step === 'login' && 'Sign in to your account'}
            {step === 'phone' && 'Add your phone number'}
            {step === '2fa' && 'Enter verification code'}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}

        {statusMessage && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-blue-700 text-sm">
            {statusMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {step === 'login' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="w-full"
                  required
                  disabled={loading}
                  autoComplete="email"
                  data-testid="login-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full"
                  required
                  disabled={loading}
                  autoComplete="current-password"
                  data-testid="login-password"
                />
              </div>
            </>
          )}

          {step === 'phone' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Phone Number</label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 123 4567"
                className="w-full"
                required
                disabled={loading}
                autoFocus
                data-testid="login-phone"
              />
              <p className="text-xs text-gray-500 mt-2">We'll send a verification code to this number every time you log in.</p>
            </div>
          )}

          {step === '2fa' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Verification Code</label>
              <Input
                type="text"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                className="w-full text-center text-2xl tracking-widest"
                required
                disabled={loading}
                autoFocus
                maxLength={6}
                data-testid="login-2fa-code"
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Didn't receive a code?{' '}
                <button type="button" onClick={() => { setTwoFACode(''); handleSubmit(new Event('submit')); }} className="text-orange-600 underline">
                  Resend
                </button>
              </p>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading || (step === '2fa' && twoFACode.length < 6) || (step === 'phone' && !phone)}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 text-lg"
            data-testid="login-submit"
          >
            {loading ? 'Verifying...' : step === '2fa' ? 'Verify Code' : step === 'phone' ? 'Save & Send Code' : 'Sign in'}
          </Button>

          {step !== 'login' && (
            <button
              type="button"
              onClick={handleBack}
              className="w-full text-sm text-gray-500 hover:text-gray-700 text-center"
            >
              Back to login
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
